import { RefObject, useCallback, useEffect, useRef, useState } from "react";

interface PostVisibilityState {
  visiblePosts: Set<string>;
  hiddenPosts: Set<string>;
}

interface UsePostVisibilityReturn {
  registerPost: (postId: string, element: HTMLElement) => void;
  unregisterPost: (postId: string) => void;
  visiblePosts: Set<string>;
  observedElementsRef: RefObject<Map<string, HTMLElement>>;
}

export const usePostVisibility = (): UsePostVisibilityReturn => {
  const [visibilityState, setVisibilityState] = useState<PostVisibilityState>({
    visiblePosts: new Set(),
    hiddenPosts: new Set(),
  });

  const observerRef = useRef<IntersectionObserver | null>(null);
  const observedElementsRef = useRef<Map<string, HTMLElement>>(new Map());

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        setVisibilityState((prevState) => {
          const newVisiblePosts = new Set(prevState.visiblePosts);
          const newHiddenPosts = new Set(prevState.hiddenPosts);

          entries.forEach((entry) => {
            const postId = entry.target.getAttribute("data-post-id");
            if (!postId) return;

            if (entry.isIntersecting) {
              newVisiblePosts.add(postId);
              newHiddenPosts.delete(postId);
            } else {
              newVisiblePosts.delete(postId);
              newHiddenPosts.add(postId);
            }
          });

          return {
            visiblePosts: newVisiblePosts,
            hiddenPosts: newHiddenPosts,
          };
        });
      },
      {
        threshold: 0.1,
        rootMargin: "0px 0px -10% 0px",
      },
    );

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const registerPost = useCallback((postId: string, element: HTMLElement) => {
    if (!observerRef.current) {
      return;
    }

    element.setAttribute("data-post-id", postId);

    observedElementsRef.current.set(postId, element);
    observerRef.current.observe(element);
  }, []);

  const unregisterPost = useCallback((postId: string) => {
    if (!observerRef.current) {
      return;
    }

    const element = observedElementsRef.current.get(postId);
    if (element) {
      observerRef.current.unobserve(element);
      observedElementsRef.current.delete(postId);
    }

    setVisibilityState((prevState) => {
      const newVisiblePosts = new Set(prevState.visiblePosts);
      const newHiddenPosts = new Set(prevState.hiddenPosts);

      newVisiblePosts.delete(postId);
      newHiddenPosts.delete(postId);

      return {
        visiblePosts: newVisiblePosts,
        hiddenPosts: newHiddenPosts,
      };
    });
  }, []);

  return {
    registerPost,
    unregisterPost,
    visiblePosts: visibilityState.visiblePosts,
    observedElementsRef,
  };
};
